import { Box, Tabs, TabList, Tab } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { portfolioOverviewBreadcrumb } from './PortfolioBreadcrumbs';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import {
  creditFacilityIdQueryParam,
  CreditFacilitySelectWithQueryParams,
} from '~/packages/credit-facility/ui/CreditFacilitySelect';

export type PortfolioTabOptionKeys =
  | 'summary'
  | 'portfolioCovenants'
  | 'financials'
  | 'borrowingBase'
  | 'profitLoss'
  | 'creditAgreement'
  | 'receivables'
  | 'strats'
  | 'reportBuilder';

type PortfolioTabType = {
  key: PortfolioTabOptionKeys;
  label: string;
  route: string;
};
const portfolioTabOptions: Record<PortfolioTabOptionKeys, PortfolioTabType> = {
  summary: {
    key: 'summary',
    label: 'Summary',
    route: `/portfolio/summary`,
  },
  portfolioCovenants: {
    key: 'portfolioCovenants',
    label: 'Portfolio Covenants',
    route: '/portfolio/portfolio-covenants',
  },
  financials: {
    key: 'financials',
    label: 'Financials',
    route: '/portfolio/financials',
  },
  borrowingBase: {
    key: 'borrowingBase',
    label: 'Borrowing Base',
    route: '/portfolio/borrowing-base',
  },
  profitLoss: {
    key: 'profitLoss',
    label: 'Profit & Loss',
    route: '/portfolio/profit-loss',
  },
  receivables: {
    key: 'receivables',
    label: 'Receivables',
    route: '/portfolio/receivables',
  },
  creditAgreement: {
    key: 'creditAgreement',
    label: 'Credit Agreement',
    route: '/portfolio/credit-agreement',
  },
  strats: {
    key: 'strats',
    label: 'Strats',
    route: '/portfolio/strats',
  },
  reportBuilder: {
    key: 'reportBuilder',
    label: 'Report Builder',
    route: '/portfolio/report-builder',
  },
};

type PortfolioTemplateProps = {
  creditFacilityId: string;
  creditFacilityName: string;
  selectedTab: PortfolioTabOptionKeys;
  children: React.ReactNode;
};
export const PortfolioTemplate = ({
  creditFacilityId,
  creditFacilityName,
  selectedTab,
  children,
}: PortfolioTemplateProps) => {
  const flags = useFeatureFlags();
  const navigate = useNavigate();

  // can configure feature flags for specific tabs as needed in the future
  // to conditionally render tabs
  const tabs = useMemo<PortfolioTabType[]>(() => {
    const tabsToDisplay = [
      portfolioTabOptions.summary,
      ...(flags.productsCreditFacilitiesPortfolioBorrowingBasePermissions ===
      'allow'
        ? [portfolioTabOptions.borrowingBase]
        : []),
      ...(flags.displayLenderPortfolioMonitoringCovenants
        ? [portfolioTabOptions.portfolioCovenants]
        : []),
      ...(flags.productsCreditFacilitiesPortfolioFinancialsPermissions ===
      'allow'
        ? [portfolioTabOptions.financials]
        : []),
      ...(flags.productsCreditFacilitiesPortfolioProfitLossPermissions ===
      'allow'
        ? [portfolioTabOptions.profitLoss]
        : []),
      ...(flags.displayLenderPortfolioMonitoringReceivables
        ? [portfolioTabOptions.receivables]
        : []),
      portfolioTabOptions.creditAgreement,
      ...(flags.productsStratsPermissions === 'allow'
        ? [portfolioTabOptions.strats]
        : []),
      ...(flags.showExploReportBuilder
        ? [portfolioTabOptions.reportBuilder]
        : []),
    ];

    return tabsToDisplay;
  }, [flags]);

  const breadcrumbs = [
    portfolioOverviewBreadcrumb,
    { label: creditFacilityName },
  ];

  const onSelectRedirectLink = (nextCreditFacilityId: string) => {
    const baseRoute = portfolioTabOptions[selectedTab].route;
    const queryParams = `?${creditFacilityIdQueryParam}=${nextCreditFacilityId}`;
    return `${baseRoute}${queryParams}`;
  };

  return (
    <Box>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Box width="50%" mt="32px">
        <CreditFacilitySelectWithQueryParams
          onSelectRedirectLink={onSelectRedirectLink}
        />
      </Box>
      <Tabs
        variant="line"
        index={tabs.findIndex(({ key }) => key === selectedTab)}
        colorScheme="blue"
        background="white"
      >
        <TabList>
          {tabs.map(({ route, label, key }) => (
            <Tab
              key={key}
              onClick={() => {
                navigate(
                  `${route}?${creditFacilityIdQueryParam}=${creditFacilityId}`,
                );
              }}
              fontSize="sm"
            >
              {label}
            </Tab>
          ))}
        </TabList>
      </Tabs>
      <Box mt="32px">{children}</Box>
    </Box>
  );
};
